/**
 * @author      Michael Hettmer <mail@michael-hettmer.de>
 * @copyright   2019 Plusbyte UG (haftungsbeschränkt)
 * @license     {@link https://plusbyte.de Plusbyte License}
 */

import { useCopyToClipboard } from '@uidotdev/usehooks';
import * as QRCode from 'qrcode';
import React, { useCallback, useEffect, useState } from 'react';

import {
  config,
  selectGameId,
  selectGameType,
  selectIsGameRunning,
  uiLogger,
  useSelector,
  useTranslation,
} from '~/app';
import { buildJoinRoute, buildSpectateRoute } from '~/app/routes';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '~/components/Dialog';
import { Button } from '~/components/Form';
import { GameType, isFreeGameType } from '~/server/src/dtos';

import { ReactComponent as CopyIcon } from './button-icons/button-copy.svg';
import { ReactComponent as QrIcon } from './button-icons/button-qr.svg';
import { ReactComponent as ShareIcon } from './button-icons/button-share.svg';

const QR_OPTIONS = {
  width: 128,
  margin: 2,
  color: { dark: '#000000', light: '#FFFFFF' as const },
};

interface InvitationDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

/** Inner content only mounted when dialog is open so state resets each time without setState in effect. */
const InvitationDialogBody: React.FC<{ onClose: () => void }> = ({ onClose: _onClose }) => {
  const { t } = useTranslation();
  const gameId = useSelector(selectGameId);
  const gameType = useSelector(selectGameType);
  const isGameRunning = useSelector(selectIsGameRunning);
  const [, copyToClipboard] = useCopyToClipboard();
  const [copySuccessJoin, setCopySuccessJoin] = useState(false);
  const [copySuccessSpectate, setCopySuccessSpectate] = useState(false);
  const [qrCodeJoinDataUrl, setQrCodeJoinDataUrl] = useState<string>('');
  const [qrCodeSpectateDataUrl, setQrCodeSpectateDataUrl] = useState<string>('');
  const [qrCodeJoinError, setQrCodeJoinError] = useState(false);
  const [qrCodeSpectateError, setQrCodeSpectateError] = useState(false);
  type TabKey = 'join' | 'spectate';
  const [activeTab, setActiveTab] = useState<TabKey>('join');

  const invitationUrl = useCallback(() => {
    if (!gameId || gameType === undefined) return '';
    if (
      isFreeGameType(gameType) ||
      gameType === GameType.TournamentBronze ||
      gameType === GameType.TournamentSilver ||
      gameType === GameType.TournamentGold
    ) {
      return `${config.siteUrl}${buildJoinRoute(gameId)}`;
    }
    return '';
  }, [gameId, gameType]);

  const spectateUrl = useCallback(() => {
    if (!gameId) return '';
    return `${config.siteUrl}${buildSpectateRoute(gameId)}`;
  }, [gameId]);

  const invitationText = useCallback(() => {
    if (gameType === undefined) return t('invitation_text_default');
    if (isFreeGameType(gameType)) {
      return t('invitation_text_free');
    } else if (
      gameType === GameType.TournamentBronze ||
      gameType === GameType.TournamentSilver ||
      gameType === GameType.TournamentGold
    ) {
      return t('invitation_text_tournament');
    }
    return t('invitation_text_default');
  }, [gameType, t]);

  const handleCopyJoin = useCallback(() => {
    const url = invitationUrl();
    if (url) {
      copyToClipboard(url);
      setCopySuccessJoin(true);
      setTimeout(() => setCopySuccessJoin(false), 2000);
    }
  }, [copyToClipboard, invitationUrl]);

  const handleCopySpectate = useCallback(() => {
    const url = spectateUrl();
    if (url) {
      copyToClipboard(url);
      setCopySuccessSpectate(true);
      setTimeout(() => setCopySuccessSpectate(false), 2000);
    }
  }, [copyToClipboard, spectateUrl]);

  const handleShare = useCallback(() => {
    const url = invitationUrl();
    const text = invitationText();

    if (navigator.share && url) {
      navigator
        .share({
          title: t('invitation_share_title'),
          text: text,
          url: url,
        })
        .catch((error) => {
          uiLogger.warn({ err: error }, 'Error sharing');
        });
    } else {
      copyToClipboard(text);
      setCopySuccessJoin(true);
      setTimeout(() => setCopySuccessJoin(false), 2000);
    }
  }, [invitationUrl, invitationText, t, copyToClipboard]);

  const handleShareSpectate = useCallback(() => {
    const url = spectateUrl();
    const text = t('invitation_text_spectate');

    if (navigator.share && url) {
      navigator
        .share({
          title: t('invitation_share_title'),
          text: text,
          url: url,
        })
        .catch((error) => {
          uiLogger.warn({ err: error }, 'Error sharing spectate link');
        });
    } else {
      copyToClipboard(url || text);
      setCopySuccessSpectate(true);
      setTimeout(() => setCopySuccessSpectate(false), 2000);
    }
  }, [spectateUrl, t, copyToClipboard]);

  // Generate QR codes when dialog is open (component only mounts when isOpen, so no reset-on-close needed)
  useEffect(() => {
    const generateQr = (url: string): Promise<string> => QRCode.toDataURL(url, QR_OPTIONS);

    const run = async () => {
      const join = invitationUrl();
      const spectate = spectateUrl();

      if (!isGameRunning && join) {
        try {
          const dataUrl = await generateQr(join);
          setQrCodeJoinDataUrl(dataUrl);
          setQrCodeJoinError(false);
        } catch (error) {
          uiLogger.warn({ err: error }, 'Error generating join QR code');
          setQrCodeJoinDataUrl('');
          setQrCodeJoinError(true);
        }
      } else {
        setQrCodeJoinDataUrl('');
        setQrCodeJoinError(false);
      }

      if (spectate) {
        try {
          const dataUrl = await generateQr(spectate);
          setQrCodeSpectateDataUrl(dataUrl);
          setQrCodeSpectateError(false);
        } catch (error) {
          uiLogger.warn({ err: error }, 'Error generating spectate QR code');
          setQrCodeSpectateDataUrl('');
          setQrCodeSpectateError(true);
        }
      }
    };

    run();
  }, [isGameRunning, invitationUrl, spectateUrl]);

  const showJoinSection = !isGameRunning && invitationUrl();
  const spectateLink = spectateUrl();

  return (
    <>
      <DialogTitle>
        {isGameRunning ? t('invitation_dialog_title_spectate_only') : t('invitation_dialog_title')}
      </DialogTitle>
      <DialogDescription>
        {isGameRunning
          ? t('invitation_dialog_description_spectate_only')
          : t('invitation_dialog_description')}
      </DialogDescription>

      <div className="mt-4 flex flex-col gap-4">
        {/* Tab bar – only when both join and spectate are available */}
        {showJoinSection && spectateLink && (
          <div
            role="tablist"
            aria-label={t('invitation_tablist_label')}
            className="flex rounded-lg border border-gray-200 bg-gray-100 p-1"
          >
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'join'}
              aria-controls="invitation-panel-join"
              id="invitation-tab-join"
              onClick={() => setActiveTab('join')}
              className={`flex-1 rounded-md px-4 py-2 text-sm font-medium transition-colors focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-white focus:outline-none ${
                activeTab === 'join'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {t('invitation_tab_join')}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'spectate'}
              aria-controls="invitation-panel-spectate"
              id="invitation-tab-spectate"
              onClick={() => setActiveTab('spectate')}
              className={`flex-1 rounded-md px-4 py-2 text-sm font-medium transition-colors focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-white focus:outline-none ${
                activeTab === 'spectate'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {t('invitation_tab_spectate')}
            </button>
          </div>
        )}

        {/* Panel: Join – shown in tab when game not started, or as single content when only join (shouldn't happen with current logic) */}
        {showJoinSection && (activeTab === 'join' || !spectateLink) && (
          <section
            id="invitation-panel-join"
            role="tabpanel"
            aria-labelledby="invitation-tab-join"
            className="flex flex-col gap-4"
            hidden={!!spectateLink && activeTab !== 'join'}
          >
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={invitationUrl()}
                readOnly
                className="flex-1 rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm"
                aria-label={t('invitation_join_link_label')}
              />
              <Button
                variant={copySuccessJoin ? 'secondary' : 'primary'}
                size="sm"
                onClick={handleCopyJoin}
                className="flex shrink-0 items-center gap-1"
              >
                <CopyIcon
                  className={`h-4 w-4 [&_path]:fill-none ${
                    copySuccessJoin ? '[&_path]:stroke-gray-700' : '[&_path]:stroke-white'
                  }`}
                />
                <span>{copySuccessJoin ? t('invitation_copied') : t('invitation_copy')}</span>
              </Button>
            </div>
            <div className="flex justify-center">
              <div className="rounded-lg border border-gray-200 bg-white p-4">
                {qrCodeJoinDataUrl ? (
                  <img
                    src={qrCodeJoinDataUrl}
                    alt={t('invitation_join_qr_alt')}
                    className="h-32 w-32"
                  />
                ) : (
                  <div className="flex h-32 w-32 flex-col items-center justify-center rounded-sm bg-gray-100 p-2">
                    <QrIcon className="mb-2 h-8 w-8 text-gray-400" />
                    {qrCodeJoinError && (
                      <p className="text-center text-xs text-gray-500">
                        {t('invitation_qr_error')}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
            <Button
              variant="primary"
              onClick={handleShare}
              className="flex w-full items-center justify-center gap-2"
            >
              <ShareIcon className="h-5 w-5" />
              <span>{t('invitation_share_button_join')}</span>
            </Button>
          </section>
        )}

        {/* Panel: Spectate – shown in tab when game not started, or as only content when game running */}
        {spectateLink && (activeTab === 'spectate' || !showJoinSection) && (
          <section
            id="invitation-panel-spectate"
            role="tabpanel"
            aria-labelledby={showJoinSection ? 'invitation-tab-spectate' : undefined}
            className="flex flex-col gap-4"
            hidden={!!(showJoinSection && activeTab !== 'spectate')}
          >
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={spectateLink}
                readOnly
                className="flex-1 rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm"
                aria-label={t('invitation_spectate_link_label')}
              />
              <Button
                variant={copySuccessSpectate ? 'secondary' : 'primary'}
                size="sm"
                onClick={handleCopySpectate}
                className="flex shrink-0 items-center gap-1"
              >
                <CopyIcon
                  className={`h-4 w-4 [&_path]:fill-none ${
                    copySuccessSpectate ? '[&_path]:stroke-gray-700' : '[&_path]:stroke-white'
                  }`}
                />
                <span>{copySuccessSpectate ? t('invitation_copied') : t('invitation_copy')}</span>
              </Button>
            </div>
            <div className="flex justify-center">
              <div className="rounded-lg border border-gray-200 bg-white p-4">
                {qrCodeSpectateDataUrl ? (
                  <img
                    src={qrCodeSpectateDataUrl}
                    alt={t('invitation_spectate_qr_alt')}
                    className="h-32 w-32"
                  />
                ) : (
                  <div className="flex h-32 w-32 flex-col items-center justify-center rounded-sm bg-gray-100 p-2">
                    <QrIcon className="mb-2 h-8 w-8 text-gray-400" />
                    {qrCodeSpectateError && (
                      <p className="text-center text-xs text-gray-500">
                        {t('invitation_qr_error')}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
            <Button
              variant="primary"
              onClick={handleShareSpectate}
              className="flex w-full items-center justify-center gap-2"
            >
              <ShareIcon className="h-5 w-5" />
              <span>{t('invitation_share_button_spectate')}</span>
            </Button>
          </section>
        )}
      </div>
    </>
  );
};

const InvitationDialog: React.FC<InvitationDialogProps> = ({ isOpen, onClose }) => {
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent size="md">
        {isOpen ? <InvitationDialogBody onClose={onClose} /> : null}
      </DialogContent>
    </Dialog>
  );
};

export default InvitationDialog;
